import { useEffect, useMemo, useRef, useState } from 'react';
import type { Process } from '../type/Process';
import type { Machine } from '../type/Machine';
import type { Material } from '../type/Material';
import type { MaterialLot } from '../type/MaterialLot';
import type { Article } from '../type/Article';
import type { ToolStateResponse } from '../api/TraceabilityResponseType';
import type { TraceabilityApiClient } from '../api/TraceabilityApiClient';
import { useTranslation } from '@/hooks/useTranslation';
import { buildProcessPayload, DEFAULT_PROCESS } from '../utils/processPayload';

export type TypeFilter = 'all' | 'standard' | 'test';

export function useProcessManager(
  companyName: string,
  traceabilityApiClient: TraceabilityApiClient | null
) {
  const { t } = useTranslation();

  const [processes, setProcesses] = useState<Process[]>([]);
  const [toolReferences, setToolReferences] = useState<string[]>([]);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialLots, setMaterialLots] = useState<MaterialLot[]>([]);
  const [machines, setMachines] = useState<Machine[]>([]);
  const [activeArticles, setActiveArticles] = useState<Article[]>([]);

  const [formProcess, setFormProcess] = useState<Process>(() => DEFAULT_PROCESS(companyName));
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const writeControllerRef = useRef<AbortController | null>(null);

  useEffect(() => () => { writeControllerRef.current?.abort(); }, []);
  useEffect(() => { setArchiveConfirm(false); }, [selectedId]);

  // Fetch lots when material changes
  useEffect(() => {
    const materialId = formProcess.material?.id;
    if (!traceabilityApiClient || !materialId) {
      setMaterialLots([]);
      return;
    }
    const ac = new AbortController();
    traceabilityApiClient
      .materialLotReadForMaterial({ materialId }, { signal: ac.signal })
      .then((res) => {
        if (ac.signal.aborted) return;
        if (res.ok && res.data?.lots) {
          setMaterialLots(res.data.lots);
        } else {
          setMaterialLots([]);
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch material lots:', err);
        setMaterialLots([]);
      });
    return () => ac.abort();
  }, [formProcess.material?.id, traceabilityApiClient]);

  useEffect(() => {
    if (!traceabilityApiClient) return;
    const ac = new AbortController();
    setIsLoading(true);
    setFormError(null);

    Promise.all([
      traceabilityApiClient.processReadAll({}, { signal: ac.signal }),
      traceabilityApiClient.toolReadAll(undefined, { signal: ac.signal }),
      traceabilityApiClient.materialReadAll({}),
      traceabilityApiClient.machineReadAll({}, { signal: ac.signal }),
      traceabilityApiClient.articleReadAll({}, { signal: ac.signal }),
    ])
      .then(([procRes, toolRes, matRes, machRes, artRes]) => {
        if (ac.signal.aborted) return;
        if (procRes.ok && procRes.data?.processes) {
          setProcesses(procRes.data.processes.map((i: { process: Process }) => i.process));
        } else {
          if (!procRes.ok && procRes.code === 401) return;
          setFormError(
            procRes.ok
              ? t('traceability.processManager.errors.noData')
              : procRes.message || t('traceability.processManager.errors.fetchFailed')
          );
        }
        if (toolRes.ok && toolRes.data?.tools) {
          const refs = toolRes.data.tools
            .map((i: ToolStateResponse) => i.reference)
            .filter((r: string | undefined | null): r is string => Boolean(r));
          setToolReferences(Array.from(new Set<string>(refs)).sort());
        }
        if (matRes.ok) {
          const mats = (matRes.data as { materials?: { material: Material }[] })?.materials;
          if (mats) setMaterials(mats.map((i: { material: Material }) => i.material).filter((m) => !m.archived));
        }
        if (machRes.ok && machRes.data?.machines) {
          setMachines(machRes.data.machines.map((i: { machine: Machine }) => i.machine));
        }
        if (artRes.ok && artRes.data?.articles) {
          setActiveArticles(
            artRes.data.articles
              .map((i: { article: Article }) => i.article)
              .filter((a: Article) => !a.archived)
          );
        }
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        console.error('Failed to fetch process manager data:', err);
        setFormError(t('traceability.processManager.errors.fetchFailed'));
      })
      .finally(() => { if (!ac.signal.aborted) setIsLoading(false); });

    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traceabilityApiClient]);

  const refreshProcesses = async (signal: AbortSignal) => {
    if (!traceabilityApiClient) return;
    setIsLoading(true);
    try {
      const res = await traceabilityApiClient.processReadAll({}, { signal });
      if (signal.aborted) return;
      if (res.ok && res.data?.processes) {
        setProcesses(res.data.processes.map((i: { process: Process }) => i.process));
      }
    } catch (err: unknown) {
      console.error('Failed to refresh processes:', err);
    } finally {
      if (!signal.aborted) setIsLoading(false);
    }
  };

  const handleAddOrUpdate = async () => {
    if (!traceabilityApiClient) {
      setFormError(t('traceability.processManager.errors.apiNotReady'));
      return;
    }
    const trimmedName = formProcess.name.trim();
    if (!trimmedName) { setFormError(t('traceability.processManager.errors.nameRequired')); return; }
    if (!formProcess.toolReference) { setFormError(t('traceability.processManager.errors.toolReferenceRequired')); return; }

    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsSaving(true);
    setFormError(null);

    try {
      const payload = buildProcessPayload(formProcess, companyName, trimmedName);
      const res = await traceabilityApiClient.processWrite({ process: payload }, { signal: ac.signal });
      if (ac.signal.aborted) return;
      if (res.ok) {
        const saved = { ...payload, id: res.data.id };
        await refreshProcesses(ac.signal);
        if (ac.signal.aborted) return;
        setFormProcess(saved);
        setSelectedId(res.data.id);
      } else {
        if (res.code === 401) return;
        setFormError(res.message || t('traceability.processManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to save process:', error);
      setFormError(t('traceability.processManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsSaving(false);
    }
  };

  const runWriteWithArchived = async (archived: boolean) => {
    if (!traceabilityApiClient || selectedId === undefined) return;
    const proc = processes.find((p) => p.id === selectedId);
    if (!proc) return;
    writeControllerRef.current?.abort();
    const ac = new AbortController();
    writeControllerRef.current = ac;
    setIsArchiving(true);
    setFormError(null);
    try {
      const res = await traceabilityApiClient.processWrite(
        { process: { ...proc, archived } },
        { signal: ac.signal }
      );
      if (ac.signal.aborted) return;
      if (res.ok) {
        setArchiveConfirm(false);
        await refreshProcesses(ac.signal);
        if (ac.signal.aborted) return;
        setSelectedId(undefined);
        setFormProcess(DEFAULT_PROCESS(companyName));
      } else {
        if (res.code === 401) return;
        setFormError(res.message || t('traceability.processManager.errors.saveFailed'));
      }
    } catch (error) {
      if (ac.signal.aborted) return;
      console.error('Failed to update process archived state:', error);
      setFormError(t('traceability.processManager.errors.saveFailed'));
    } finally {
      if (!ac.signal.aborted) setIsArchiving(false);
    }
  };

  const handleArchive = async () => { if (archiveConfirm) await runWriteWithArchived(true); };
  const handleRestore = () => runWriteWithArchived(false);

  const handleSelectProcess = (process: Process) => {
    setFormError(null);
    setFormProcess(process);
    setSelectedId(process.id);
  };

  const handleNewProcess = () => {
    setFormError(null);
    setFormProcess(DEFAULT_PROCESS(companyName));
    setSelectedId(undefined);
  };

  const handleDuplicate = () => {
    setFormProcess((prev) => ({ ...prev, id: undefined, name: `Copy of ${prev.name}` }));
    setSelectedId(undefined);
  };

  // Duplicate a specific process directly (e.g. from a list row) without relying on formProcess state
  const handleDuplicateFrom = (proc: Process) => {
    setFormError(null);
    setFormProcess({ ...proc, id: undefined, name: `Copy of ${proc.name}` });
    setSelectedId(undefined);
  };

  const selectedProcess = processes.find((p) => p.id === selectedId);
  const isEditing = formProcess.id !== undefined;

  const visibleProcesses = useMemo(() => {
    let list = showArchived ? processes : processes.filter((p) => !p.archived);
    if (typeFilter !== 'all') list = list.filter((p) => p.processType === typeFilter);
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter((p) => p.name.toLowerCase().includes(q));
    }
    return list;
  }, [processes, showArchived, typeFilter, searchQuery]);

  const activeProcesses = useMemo(() => processes.filter((p) => !p.archived), [processes]);

  const groupedProcesses = useMemo(() => {
    const map = new Map<string, Process[]>();
    const sorted = [...visibleProcesses].sort((a, b) => a.name.localeCompare(b.name));
    for (const proc of sorted) {
      const key = proc.toolReference || '';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(proc);
    }
    return new Map([...map.entries()].sort(([a], [b]) => a.localeCompare(b)));
  }, [visibleProcesses]);

  return {
    processes, toolReferences, materials, materialLots, machines, activeArticles,
    formProcess, setFormProcess, selectedId, formError,
    isLoading, isSaving, isArchiving, archiveConfirm, setArchiveConfirm,
    showArchived, setShowArchived, searchQuery, setSearchQuery, typeFilter, setTypeFilter,
    selectedProcess, isEditing, visibleProcesses, activeProcesses, groupedProcesses,
    handleAddOrUpdate, handleArchive, handleRestore,
    handleSelectProcess, handleNewProcess, handleDuplicate, handleDuplicateFrom,
  };
}
